import { Loader } from '@jutro/components';
import { TranslatorContext } from '@jutro/locale';
import { withAuthenticationContext } from '@xengage/gw-digital-auth-react';
import { isCapabilityEnabled } from '@xengage/gw-portals-config-js';
import { DependencyProvider } from '@xengage/gw-portals-dependency-react';
import { LobIconUtil } from '@xengage/gw-portals-util-js';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
 
import appConfig from 'app-config';
import { DocumentsPage } from 'gw-capability-document-react';
import { PolicyService, UserService } from 'gw-capability-gateway';
import { PolicyClaims } from 'gw-capability-gateway-claim-react';
import { PolicyCommission } from 'gw-capability-gateway-commission-react';
import { GatewayDocumentService } from 'gw-capability-gateway-document';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import CommonRoutes from '../../Common/CommonRouting';
import pageRouting from '../../Common/Policies-config.json5';
import FormattedHeaderComponent from '../../Components/FormattedHeaderComponent/FormattedHeaderComponent';
import messages from '../Policies.messages';
import styles from '../Policies.module.scss';
import metadata from './PolicyDetails.metadata.json5';

const { capabilitiesConfig } = appConfig;

const DocumentPageService = {
    getDocuments: PolicyService.getDocumentsForPolicy,
    generateUploadToken: GatewayDocumentService.generateUploadToken,
    uploadDocument: GatewayDocumentService.uploadDocument,
    removeDocument: GatewayDocumentService.removeDocument,
    downloadDocument: GatewayDocumentService.downloadDocument,
};

function documentFeatureTurnedOn(routes) {
    const documentRoute = {
        path: '/documents',
        component: DocumentsPage,
    };

    if (
        isCapabilityEnabled({ capabilitiesConfig, capabilityName: 'document' })
    ) {
        routes.push(documentRoute);
    }
}

class PolicyDetails extends Component {
    static contextType = TranslatorContext;

    static propTypes = {
        match: PropTypes.shape({
            path: PropTypes.string,
            params: PropTypes.shape({
                policyNumber: PropTypes.string,
            }),
            url: PropTypes.string,
        }).isRequired,
        authHeader: PropTypes.shape({}).isRequired,
        authenticated: PropTypes.bool.isRequired,
        location: PropTypes.shape({
            state: PropTypes.string,
            pathname: PropTypes.string,
        }).isRequired,
    };

    state = {
        policyResponse: {},
        policyBillingData: {},
        currentView: undefined,
        isLoading: true,
        commissionTurnedOn: false,
    };

    componentDidMount() {
        this.getPolicyDetails();
    }

    getAccountHolderNameandNumber = (accountData, type) => {
        if (_.isEmpty(accountData)) {
            return null;
        }

        if (type === 'status') {
            return accountData.latestPeriod.displayStatus;
        }

        return `${accountData.product.productName} (${accountData.policyNumber})`;
    };

    getRoutesWithFeature() {
        const routes = [...pageRouting];

        this.billingFeatureTurnedOn(routes);
        this.hasAccessForCommision(routes);
        this.claimFeatureTurnedOn(routes);
        documentFeatureTurnedOn(routes);

        return routes;
    }

    billingFeatureTurnedOn = routes => {
        if (
            isCapabilityEnabled({
                capabilitiesConfig,
                capabilityName: 'billing',
            })
        ) {
            const billingRoute = {
                path: '/billing',
                componentPath: 'Policies/Billing/Billing',
            };

            routes.push(billingRoute);
        }
    };

    hasAccessForCommision = routes => {
        const { commissionTurnedOn } = this.state;

        if (
            commissionTurnedOn &&
            isCapabilityEnabled({
                capabilitiesConfig,
                capabilityName: 'commission',
            })
        ) {
            const commissionRoute = {
                path: '/commission',
                component: PolicyCommission,
            };

            routes.push(commissionRoute);
        }
    };

    claimFeatureTurnedOn = routes => {
        if (this.isClaimsTurnedOn()) {
            const claimRoute = {
                path: '/claims',
                component: PolicyClaims,
            };

            routes.push(claimRoute);
        }
    };

    updateCountInfo = type => {
        const { policyResponse } = this.state;
        const currentCount = _.get(policyResponse, `numberOf${type}`);

        _.set(policyResponse, `numberOf${type}`, currentCount + 1);
        this.setState({ policyResponse });
    };

    updateDocumentCount = (count, type = 'Documents') => {
        const { policyResponse } = this.state;

        _.set(policyResponse, `numberOf${type}`, count);
        this.setState({ policyResponse });
    };

    getPolicyDetails = async () => {
        const {
            authenticated,
            authHeader,
            match: {
                params: { policyNumber },
            },
            location: { state: redirectPath, pathname },
            auth
        } = this.props;

        if (authenticated || auth?.isAuthenticated) {
            this.getCommissionTurnedOn(authHeader);

            const getPolicyResponse = await PolicyService.getPolicy(
                policyNumber,
                authHeader
            );
            const routePath = pathname && (pathname.trim().split('/') || []);
            const selectedPageFromPath = routePath[routePath.length - 1];
            const activeTile =
                selectedPageFromPath || redirectPath || 'summary';

            this.setState({
                currentView: activeTile,
                policyResponse: getPolicyResponse,
            });
        }

        await PolicyService.getPolicyBillingSummaryInfo(
            policyNumber,
            authHeader
        ).then((billingResponseData = {}) => {
            if (
                !_.isUndefined(billingResponseData.policyPeriodBillingSummaries)
            ) {
                const billingData =
                    billingResponseData.policyPeriodBillingSummaries;
                const billingIcon = _.get(billingData, '[0].isDelinquent')
                    ? 'mi-warning'
                    : 'mi-payment';

                this.setState({
                    policyBillingData: billingResponseData,
                    billingIcon,
                });
            } else {
                const billingIcon = 'mi-payment';

                this.setState({
                    billingIcon,
                });
            }
        });
        await PolicyService.addRecentlyViewedPolicy(policyNumber, authHeader);
        this.setState({
            isLoading: false,
        });
    };

    getCommissionTurnedOn = async authHeader => {
        const permissionDTO = {
            permission: 'viewcommissions',
        };
        const commissionTurnedOn = await UserService.hasUserSystemPermission(
            permissionDTO,
            authHeader
        );

        this.setState({ commissionTurnedOn });
    };

    isClaimsTurnedOn = () => isCapabilityEnabled({
            capabilitiesConfig,
            capabilityName: 'claim',
        });

    handleTilesOnClick = id => {
        this.setState({ currentView: id });
    };

    render() {
        const {
            policyResponse,
            currentView,
            isLoading,
            billingIcon,
            commissionTurnedOn,
            policyBillingData,
        } = this.state;
        const productName = _.get(policyResponse, 'product.productCode');

        if (isLoading) {
            return <Loader loaded={!isLoading} />;
        }

        if (_.isEmpty(policyResponse)) {
            return null;
        }

        const overrides = {
            [currentView]: {
                active: true,
            },
            commission: {
                active: currentView === 'commission',
                visible:
                    commissionTurnedOn &&
                    isCapabilityEnabled({
                        capabilitiesConfig,
                        capabilityName: 'commission',
                    }),
            },
            billing: {
                active: currentView === 'billing',
                tileIcon: billingIcon,
                visible: isCapabilityEnabled({
                    capabilitiesConfig,
                    capabilityName: 'billing',
                }),
            },
            // DE16776
            claims: {
                active: currentView === 'claims' && this.props.history.location?.state?.redirectPath !== '/summary',
                visible: this.isClaimsTurnedOn(),
            },
            summary:{
                active: currentView === 'summary' || this.props.history.location?.state?.redirectPath === '/summary',
            },
            policyProductName: {
                content: this.getAccountHolderNameandNumber(policyResponse, ''),
            },
            policyStatus: {
                message: this.getAccountHolderNameandNumber(
                    policyResponse,
                    'status'
                ),
            },
            accountHeader: {
                redirectPath: `/accounts/${policyResponse.account.accountNumber}/summary`,
                displayName: policyResponse.account.accountHolder.displayName,
                message: messages.accountNavigation,
            },
            policyIcon: {
                icon: LobIconUtil.getMaterialIcon(productName),
            },
        };

        const resolvers = {
            resolveComponentMap: {
                accountheadercomponent: FormattedHeaderComponent,
            },
            resolveCallbackMap: {
                handleTilesOnClick: this.handleTilesOnClick,
            },
            resolveClassNameMap: styles,
        };
        const {
            match: { url }
        } = this.props;
        
        const policyDetailsData = {
            policyResponse,
            getUpdateCountInfo: this.updateCountInfo,
        };

        return (
            <DependencyProvider value={{ DocumentPageService }}>
                <ViewModelForm
                    uiProps={metadata.pageContent}
                    model={policyResponse}
                    overrideProps={overrides}
                    callbackMap={resolvers.resolveCallbackMap}
                    componentMap={resolvers.resolveComponentMap}
                    classNameMap={resolvers.resolveClassNameMap}
                />
                <CommonRoutes
                    steps={this.getRoutesWithFeature()}
                    basePath={url}
                    setDocumentCount={this.updateDocumentCount}
                    policyNumber={policyResponse.policyNumber}
                    productCode={policyResponse.product.productCode}
                    policyDetailsData={policyDetailsData}
                    billingData={policyBillingData}
                />
            </DependencyProvider>
        );
    }
}
export default withRouter(withAuthenticationContext(PolicyDetails));
